import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Tag } from "lucide-react";

const FEATURE_FLAGS = [
  { k: "research_ideas", l: "Research Idea Center" },
  { k: "paper_explainers", l: "Paper Explainers" },
  { k: "datasets_download", l: "Dataset Downloads" },
  { k: "discussions", l: "Thread Creation" },
  { k: "lms_courses", l: "LMS Courses" },
  { k: "events_priority", l: "Event Discounts" },
];

const EMPTY = {
  code: "", name: "", description: "",
  price_npr_monthly: "", price_npr_annual: "",
  features: [], feature_flags: [],
  is_active: true, display_order: "", cta_label: "Choose plan",
};

export default function AdminPricingPlansPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/subscription-plans"); setItems(r.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and name required"); return; }
    try {
      if (editing === "new") await api.post("/admin/subscription-plans", form);
      else await api.put(`/admin/subscription-plans/${editing}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this plan? Existing subscribers won't be affected.")) return;
    await api.delete(`/admin/subscription-plans/${id}`); toast.success("Deleted"); load();
  };

  const toggleFlag = (flag) => {
    const s = new Set(form.feature_flags || []);
    s.has(flag) ? s.delete(flag) : s.add(flag);
    setForm({ ...form, feature_flags: Array.from(s) });
  };

  return (
    <div data-testid="admin-plans-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUBSCRIPTION PLANS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Control <em className="italic text-kb-primary">pricing & access</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Configure plans (Free, Standard, Premium…), their NPR prices, the bullet list shown on /pricing, and which feature flags they unlock.</p>
        </div>
        <button onClick={() => { setEditing("new"); setForm(EMPTY); }} className="kb-btn-primary text-sm" data-testid="plan-new-btn"><Plus className="w-3.5 h-3.5" /> New plan</button>
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="plan-editor">
          <div className="flex items-center justify-between mb-3">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{editing === "new" ? "NEW PLAN" : "EDITING"}</div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="kb-input text-sm" placeholder="Plan code (free, standard, premium) *"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
              disabled={editing !== "new"} data-testid="plan-code-input" />
            <input className="kb-input text-sm" placeholder="Plan name *"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name-input" />
            <textarea rows={2} className="kb-input text-sm sm:col-span-2 resize-none" placeholder="Short description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input type="number" min="0" step="1" className="kb-input text-sm" placeholder="Monthly price in NPR (e.g. 999)"
              value={form.price_npr_monthly} onChange={(e) => setForm({ ...form, price_npr_monthly: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 0)) })}
              data-testid="plan-price-monthly" />
            <input type="number" min="0" step="1" className="kb-input text-sm" placeholder="Annual price in NPR (e.g. 9999)"
              value={form.price_npr_annual} onChange={(e) => setForm({ ...form, price_npr_annual: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 0)) })}
              data-testid="plan-price-annual" />
            <input type="number" min="1" step="1" className="kb-input text-sm" placeholder="Sort order on /pricing (lower = left)"
              value={form.display_order} onChange={(e) => setForm({ ...form, display_order: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || "")) })} />
            <input className="kb-input text-sm" placeholder="Button label (e.g. 'Choose Standard')"
              value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-kb-ink-muted sm:col-span-2">
              <input type="checkbox" checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active (visible on /pricing)
            </label>
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Marketing bullet list (one per line)</label>
              <textarea rows={5} className="kb-input text-sm w-full resize-none mt-1"
                value={(form.features || []).join("\n")}
                onChange={(e) => setForm({ ...form, features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                data-testid="plan-features-textarea" />
            </div>
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Feature flags unlocked</label>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                {FEATURE_FLAGS.map((f) => (
                  <label key={f.k} className="flex items-center gap-2 text-sm text-kb-ink p-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={(form.feature_flags || []).includes(f.k)}
                      onChange={() => toggleFlag(f.k)} data-testid={`plan-flag-${f.k}`} />
                    <span>{f.l}</span>
                    <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted ml-auto">{f.k}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="plan-save-btn">Save plan</button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id || p.code} className="kb-card p-5" data-testid={`admin-plan-${p.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Tag className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-kb-ink mt-2">{p.name}</h3>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">{p.code} · order {p.display_order}</div>
                  <div className="mt-3 text-sm text-kb-ink">
                    NPR {Number(p.price_npr_monthly || 0).toLocaleString()}<span className="kb-mono text-[10px] text-kb-ink-muted">/mo</span>
                    <span className="text-kb-ink-muted ml-2">· NPR {Number(p.price_npr_annual || 0).toLocaleString()}<span className="kb-mono text-[10px]">/yr</span></span>
                  </div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider mt-3 text-kb-ink-light">
                    {(p.feature_flags || []).join(", ") || "no flags"}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => { setEditing(p.id); setForm({ ...EMPTY, ...p }); }} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid={`admin-plan-${p.code}-edit`}><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-rose-600 hover:text-rose-700" data-testid={`admin-plan-${p.code}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}
